import { createPostSchema } from '$shared/api';
import {
  defaults,
  setMessage,
  superForm,
  type Infer,
  type SuperForm,
} from 'sveltekit-superforms/client';
import { zod } from 'sveltekit-superforms/adapters';
import * as flashModule from 'sveltekit-flash-message';
import { client } from '$lib/api/Client';
import { ClientResponseError } from '$lib/api/ClientResponseError';
import type { Post } from '$shared/lib';
import { Editor } from '@tiptap/core';
import { useQueryClient } from '@tanstack/svelte-query';
import { browser } from '$app/environment';
import { get, writable, type Writable } from 'svelte/store';
import { getTipTapProps } from '$lib/utils/tiptap';
import { goto } from '$app/navigation';
import { cn } from '$lib/utils/misc';

export type PostEditorOpenState =
  | 'fullscreen'
  | 'normal'
  | 'minimized'
  | 'hidden';
export type PostEditorState = 'creating' | 'editing';

export type PostEditor = {
  reset: () => void;
  close: () => void;
  editor: Editor;
  superform: ReturnType<typeof getOrCreateSuperForm>;
  isInitialized: boolean;
  activePost: Writable<Post | null>;
  openState: Writable<PostEditorOpenState>;
  editorHeight: Writable<number>;
};

export const EDITOR_HEIGHTS = {
  normal: 475,
  minimized: 70,
};

export const MIN_TEXT_AREA_HEIGHT = 130;

let superform: SuperForm<Infer<typeof createPostSchema>> | null = null;
let editor: Editor | null = null;
let redirectTo: string | null = null;
let isInitialized = false;
const openState = writable<PostEditorOpenState>('hidden');
const editorHeight = writable(EDITOR_HEIGHTS.normal);
const activePost = writable<Post | null>(null); // Only used for editing

function createSuperForm() {
  const queryClient = useQueryClient();
  superform = superForm(defaults(zod(createPostSchema)), {
    id: crypto.randomUUID(),
    dataType: 'json',
    validators: zod(createPostSchema),
    validationMethod: 'onsubmit',
    SPA: true,
    resetForm: false,
    invalidateAll: 'force',
    flashMessage: { module: flashModule },
    syncFlashMessage: true,
    async onUpdate({ form }) {
      if (!form.valid) return;
      const post = get(activePost);

      try {
        if (post?.id) {
          await client.forums.updatePost({
            ...form.data,
            id: post.id,
          });
          setMessage(form, {
            status: 'success',
            title: 'POST UPDATED',
            description: 'Post updated successfully.',
          });
        } else {
          const { postId } = await client.forums.createPost(form.data);
          setMessage(form, {
            status: 'success',
            title: 'POST CREATED',
            description: 'Post created successfully.',
          });
          redirectTo = `/posts/${postId}`;
        }

        // Invalidate the post and posts queries
        await queryClient.invalidateQueries({
          queryKey: ['post', String(post?.id)],
        });
        await queryClient.invalidateQueries({ queryKey: ['posts'] });

        // Reset the form and editor
        superform?.reset();
        editor?.commands.clearContent();

        // Remove the form state from localStorage
        localStorage.removeItem('post-editor-form');

        // Close the editor
        openState.set('hidden');
      } catch (error) {
        console.error(error);
        if (error instanceof ClientResponseError) {
          setMessage(form, {
            status: 'error',
            title: 'Unable to create post',
            description: error.message,
          });
        } else {
          setMessage(form, {
            status: 'error',
            title: 'An unexpected error occurred.',
            description: 'Please try again later.',
          });
        }
      }
    },
    onUpdated: () => {
      // Redirects must be handled here to allow the superform
      // onUpdate callback to finish before the redirect
      if (redirectTo) {
        goto(redirectTo);
        redirectTo = null;
      }
    },
  });

  return superform;
}

function createPostEditor(
  superform: SuperForm<Infer<typeof createPostSchema>>,
) {
  const { form } = superform;
  const { extensions, editorProps } = getTipTapProps();
  editor = new Editor({
    onUpdate: ({ editor }) => {
      form.update((form) => {
        const editorContent = editor.getJSON();
        form.body = {
          type: editorContent.type ?? 'doc',
          content: editorContent.content ?? [],
        };
        return form;
      });
    },
    extensions,
    editorProps: {
      ...editorProps,
      attributes: {
        ...editorProps.attributes,
        class: cn(editorProps.attributes.class, 'p-3'),
        id: 'post-editor',
      },
      handlePaste: (view, event) => {
        // If clipboard has an image, let our custom handler deal with it
        // See PostEditorTextArea.svelte for the custom handler
        const items = event.clipboardData?.items;
        if (
          items &&
          Array.from(items).some((item) => item.type.startsWith('image/'))
        ) {
          return true;
        }
        return false;
      },
    },
  });

  return editor;
}

function getOrCreateSuperForm() {
  if (superform) return superform;
  return createSuperForm();
}

function getOrCreatePostEditor(
  superform: SuperForm<Infer<typeof createPostSchema>>,
) {
  if (editor) return editor;
  return createPostEditor(superform);
}

export function openCreatePost() {
  // Clear the active post (if any)
  activePost.set(null);

  // Prevent the user from clearing the form state if the
  // editor is already open and bring it into view
  if (get(openState) !== 'hidden') {
    openState.set('normal');
    return;
  }

  // Reset the form and editor
  if (superform) {
    const { form } = superform;
    superform.reset();
    editor?.commands.setContent(get(form).body);
  }

  // Open the editor
  openState.set('normal');
}

export function openEditPost(post: Post) {
  // Set the active post
  activePost.set(post);

  // Reset the form and editor
  superform?.reset({
    data: {
      title: post.title,
      body: post.body,
      tags: post.tags,
      forumName: post.forum.name,
      forumType: post.forum.type,
    },
  });
  editor?.commands.setContent(post.body);

  // Open the editor
  openState.set('normal');
}

export function getPostEditor(): PostEditor {
  if (!browser) {
    throw new Error('Post editor is not available on the server');
  }

  const superform = getOrCreateSuperForm();
  const editor = getOrCreatePostEditor(superform);

  // Save the form state to localStorage when the form is updated
  // Only save if creating a new post
  const { form } = superform;
  form.subscribe((form) => {
    const isCreatingPost = !get(activePost);
    if (superform?.isTainted() && isCreatingPost) {
      localStorage.setItem('post-editor-form', JSON.stringify(form));
    }
  });

  if (!isInitialized) {
    const storedFormState = localStorage.getItem('post-editor-form');
    if (storedFormState) {
      const initialFormState = JSON.parse(storedFormState);
      form.set(initialFormState);
      editor?.commands.setContent(get(form).body);
      openState.set('minimized');
    }
  }

  function reset() {
    superform?.reset();
    if (editor) {
      editor.commands.clearContent();
    }
  }

  function close() {
    openState.set('hidden');
    reset();
    localStorage.removeItem('post-editor-form');
  }

  isInitialized = true;

  return {
    reset,
    close,
    activePost,
    openState,
    editor,
    superform,
    editorHeight,
    isInitialized: true,
  };
}
